@file:Suppress("UNUSED_PARAMETER")

package ui.device.detail.preview

import androidx.compose.material3.SnackbarDuration
import androidx.compose.runtime.*
import common.frontend.api.*
import compose.assets.Svg
import compose.assets.svg.*
import device.DevicePlaylist
import io.Repository
import kotlinx.coroutines.async
import kotlinx.coroutines.launch
import ssoft.frontend.generated.resources.*
import tk.mallumo.common.msg.Message
import tk.mallumo.compose.navigation.LocalNavigation
import tk.mallumo.compose.navigation.navTo_ConsoleUI
import tk.mallumo.compose.navigation.viewmodel.NavigationViewModel
import tk.mallumo.compose.navigation.viewmodel.VM
import tk.mallumo.utils.DataState
import tk.mallumo.utils.mutableDataState
import tools.TabEntry
import tools.await
import tools.findRoot
import tools.printStackTraceInDebug
import ui.console.ConsoleArgs
import ui.snackBarStateGlobal

@VM
class DevicePreviewVM : NavigationViewModel() {

    var tabs by mutableStateOf(listOf<TabEntry<TabType>>())
        private set

    private lateinit var _selectedTab: MutableState<TabEntry<TabType>>
    val selectedTab: State<TabEntry<TabType>> get() = _selectedTab


    private lateinit var scopeUi: DevicePreviewScope

    var state by mutableDataState<DevicePreviewScope.Wrapper>()
        private set

    enum class TabType {
        INFO,
        LAYER_PRESENTATION,
        LAYER_APPLICATION,
        ACTION,
        REPORT_SYSTEM,
    }

    sealed interface Action {
        data object Delete : Action
        data object ReorderPlaylist : Action
        data object ReorderApps : Action
        sealed class DevAction(val key: Message.Task.Keys) : Action {
            data object Update : DevAction(Message.Task.Keys.UPDATE)
            data object RebootDevice : DevAction(Message.Task.Keys.REBOOT_DEVICE)
            data object RestartApp : DevAction(Message.Task.Keys.RESTART_APP)
            data object ResendRegistration : DevAction(Message.Task.Keys.REGISTRATION)
            data object ScreenshotApp : DevAction(Message.Task.Keys.UNKNOWN)
            data object ScreenshotDisplay : DevAction(Message.Task.Keys.UNKNOWN)
            data object SendConfig : DevAction(Message.Task.Keys.CONFIGURATION)
            data object SendApps : DevAction(Message.Task.Keys.APPS)
            data object SendPlaylist : DevAction(Message.Task.Keys.PLAYLIST)
            data object RemoteConsole : DevAction(Message.Task.Keys.UNKNOWN)
        }

        companion object {

            @Composable
            fun rememberAction(vm: DevicePreviewVM): (Action) -> Unit {
                val nav = LocalNavigation.current
                return remember {
                    { act ->
                        when (act) {
                            Delete -> vm.delete()
                            DevAction.RebootDevice,
                            DevAction.RestartApp,
                            DevAction.SendConfig,
                            DevAction.SendPlaylist,
                            DevAction.ScreenshotApp,
                            DevAction.ScreenshotDisplay,
                            DevAction.SendApps,
                            DevAction.ResendRegistration,
                            DevAction.Update -> vm.deviceAction(act as DevAction)

                            DevAction.RemoteConsole -> {
                                vm.state.entry?.data?.mac?.also {
                                    nav.findRoot().navTo_ConsoleUI(ConsoleArgs(it))
                                }
                            }

                            ReorderPlaylist -> vm.reorderPlaylist()
                            ReorderApps -> vm.reorderApps()
                        }
                    }
                }
            }
        }
    }

    fun init(scopeUi: DevicePreviewScope) {
        this.scopeUi = scopeUi
        state = DataState.Loading()

        scope.launch {
            runCatching {
                val data = Repository.api.device.info(scopeUi.args.deviceId, scopeUi.lang)

                val taskApps = async {
                    runCatching {
                        if (!data.config.layer.bottom) error("bottom layer not enabled ")
                        Repository.api.app.perDeviceListing(data.company.id, scopeUi.args.deviceId)
                    }.onFailure { it.printStackTraceInDebug() }
                        .getOrNull() ?: Io.ListingApp()
                }

                val taskPlaylist = async {
                    runCatching {
                        if (!data.config.layer.top) error("top layer not enabled")
                        Repository.api.playlist.single(data.id)
                    }.onFailure { it.printStackTraceInDebug() }
                        .getOrNull() ?: DevicePlaylist.empty(data.id, 1, true)
                }

                configureTabs(scopeUi, data, scopeUi.login)
                state = DataState.Result(
                    entry = DevicePreviewScope.Wrapper(
                        data = data,
                        apps = taskApps.await(),
                        playlist = taskPlaylist.await(),
                        login = scopeUi.login,
                    )
                )
            }.onFailure {
                it.printStackTraceInDebug()
                state = DataState.Error(it.message!!, throwable = it)
            }
        }
    }


    private fun reorderPlaylist() {
        state.entry?.apply {
            scope.launch {
                runCatching {
                    Repository.api.playlist.reorder(
                        deviceId = data.id,
                        newOrder = adapterPlaylist.extract()
                    )
                }.onSuccess {
                    adapterPlaylist.applyChanges()
                }.onFailure {
                    adapterPlaylist.revokeChanges()
                    it.printStackTraceInDebug()
                    snackBarStateGlobal.showSnackbar(
                        message = it.message!!,
                        withDismissAction = true,
                        duration = SnackbarDuration.Long
                    )
                }
            }
        }
    }

    private fun reorderApps() {
        state.entry?.apply {
            scope.launch {
                runCatching {
                    Repository.api.app.perDeviceReorder(
                        companyId = data.company.id,
                        deviceId = data.id,
                        appIds = adapterApps.extract()
                    )
                }.onSuccess {
                    adapterApps.applyChanges()
                }.onFailure {
                    adapterApps.revokeChanges()
                    it.printStackTraceInDebug()
                    snackBarStateGlobal.showSnackbar(
                        message = it.message!!,
                        withDismissAction = true,
                        duration = SnackbarDuration.Long
                    )
                }
            }
        }
    }

    private fun deviceAction(act: Action.DevAction) {
        scope.launch {
            runCatching {
                Repository.api.device.action(scopeUi.args.deviceId, act.key.name)
            }.onFailure {
                it.printStackTraceInDebug()
                snackBarStateGlobal.showSnackbar(
                    message = it.message!!,
                    withDismissAction = true,
                    duration = SnackbarDuration.Long
                )
            }.onSuccess {
                snackBarStateGlobal.showSnackbar(
                    message = Res.string.device_detail__cmd_send_ok.await()
                )
            }
        }
    }

    private fun delete() {
        scope.launch {
            runCatching {
                Repository.api.device.delete(scopeUi.args.deviceId)
            }.onSuccess {
                scopeUi.nav.up()
            }.onFailure {
                it.printStackTraceInDebug()
                snackBarStateGlobal.showSnackbar(
                    message = it.message!!,
                    withDismissAction = true,
                    duration = SnackbarDuration.Long
                )
            }
        }
    }

    private fun configureTabs(scopeUi: DevicePreviewScope, device: Io.DeviceInfo, login: Io.User) {
        tabs = buildTabs(scopeUi, device, login) {
            _selectedTab.value = it
        }
        if (!::_selectedTab.isInitialized || tabs.none { it.tag == _selectedTab.value.tag }) {
            _selectedTab = mutableStateOf(tabs.first())
        }

    }

    private fun buildTabs(
        scopeUi: DevicePreviewScope,
        device: Io.DeviceInfo,
        login: Io.User,
        onClick: (TabEntry<TabType>) -> Unit
    ): List<TabEntry<TabType>> {
        return buildList {
            add(
                TabEntry(
                    icon = Svg.Information_slab_circle_outline,
                    label = Res.string.device_detail__info__title,
                    onClick = onClick,
                    tag = TabType.INFO
                )
            )
            if (login.canLayerTopView(device.company.id) && device.config.layer.top) {
                add(
                    TabEntry(
                        icon = Svg.Animation_play,
                        label = Res.string.device_detail__layer_top__title,
                        onClick = onClick,
                        tag = TabType.LAYER_PRESENTATION
                    )
                )
            }
            if (login.canLayerBottomView(device.company.id) && device.config.layer.bottom) {
                add(
                    TabEntry(
                        icon = Svg.Square_rounded_badge_outline,
                        label = Res.string.device_detail__layer_bottom__title,
                        onClick = onClick,
                        tag = TabType.LAYER_APPLICATION
                    )
                )
            }
            if (login.canDeviceActionsView(device.company.id)) {
                add(
                    TabEntry(
                        icon = Svg.Cog_play_outline,
                        label = Res.string.device_detail__actions__title,
                        onClick = onClick,
                        tag = TabType.ACTION
                    )
                )
            }

            if (login.canReportSystemView(device.company.id)) {
                add(
                    TabEntry(
                        icon = Svg.Chart_timeline,
                        label = Res.string.device_detail__report_system__title,
                        onClick = onClick,
                        tag = TabType.REPORT_SYSTEM
                    )
                )
            }
        }
    }

    override fun onRelease() {
        state = DataState.Idle()
    }

}


